// =============================================================================
// TerminalChargeModal — "Afrekenen op pinterminal" from BookingDetailScreen
// =============================================================================
// Thin modal shell around TerminalChargePanel for an existing confirmed-unpaid
// booking. onClose(true) once the payment settled so the caller reloads.
// =============================================================================
import { useCallback, useEffect, useRef, useState } from 'react';
import { TerminalChargePanel, type TerminalChargeHandle } from './TerminalChargePanel';

interface Props {
  bookingId: string;        // parent booking id (booking.bookings.id)
  bookingNumber: string;
  // departure id - enables "Boeking annuleren" in the panel
  departureId?: string | null;
  onClose: (changed: boolean) => void;
}

export function TerminalChargeModal({ bookingId, bookingNumber, departureId = null, onClose }: Props) {
  const [settled, setSettled] = useState(false);
  const panel = useRef<TerminalChargeHandle>(null);

  // Closing the window is an exit from the payment just like "Annuleren" is:
  // free the reader first, or the WisePOS keeps showing the amount until some
  // later sale trips over it (and is refused with terminal_reader_busy).
  // Never block the close on it — a stuck reader must not trap the operator.
  const closeAfterClearing = useCallback(
    async (changed: boolean) => {
      await panel.current?.clearReader();
      onClose(changed);
    },
    [onClose],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') void closeAfterClearing(settled);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [closeAfterClearing, settled]);

  return (
    <div className="dep-modal-backdrop" onClick={() => { /* ✕ / Esc only */ }}>
      <div className="dep-modal cancel-modal" onClick={(e) => e.stopPropagation()}>
        <header className="dep-modal-head">
          <div>
            <h2>Afrekenen op pinterminal</h2>
            <div className="dep-modal-sub muted">{bookingNumber}</div>
          </div>
          <button
            className="btn-link dep-modal-close"
            onClick={() => void closeAfterClearing(settled)}
            aria-label="Sluiten"
          >✕</button>
        </header>
        <div className="dep-modal-body">
          <TerminalChargePanel
            ref={panel}
            bookingId={bookingId}
            bookingNumber={bookingNumber}
            onSettled={() => setSettled(true)}
            onAbandon={() => onClose(false)}
            cancelDepartureId={departureId}
            onCancelledBooking={() => onClose(true)}
          />
          {settled ? (
            <div className="walkin-step-actions">
              <button className="walkin-primary-btn" onClick={() => onClose(true)} autoFocus>
                Sluiten
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
